import type {
  DecisionAgentDomain,
  DecisionAgentFreshnessCadence,
  DecisionAgentMissingBehavior,
  DecisionAgentRequiredFor,
} from '../decision-agent-platform';
import type { DecisionAgentSourceClass } from './sourceClasses';

export type ModuleEvidenceContractEntry<
  EvidenceKey extends string = string,
  ContextKind extends string = string,
  SchemaVersion extends string = string,
  BindingPlaceholder extends string = string,
> = Readonly<{
  evidenceKey: EvidenceKey;
  stateKey: EvidenceKey;
  sourceClass: DecisionAgentSourceClass;
  contextKind: ContextKind;
  schemaVersion: SchemaVersion;
  bindingPlaceholders: readonly BindingPlaceholder[];
  domain: DecisionAgentDomain;
  freshnessCadence: DecisionAgentFreshnessCadence;
  requiredFor: readonly DecisionAgentRequiredFor[];
  missingBehavior: DecisionAgentMissingBehavior;
  description: string;
}>;

export type ModuleContractKeySpaces = Readonly<{
  evidenceKeys: readonly string[];
  sourceClasses: readonly string[];
  contextKinds: readonly string[];
  schemaVersions: readonly string[];
  stateKeys: readonly string[];
  bindingPlaceholders: readonly string[];
}>;

// A manifest-required evidence key whose authority is NOT an
// AskHelixMemoryArtifact substrate (e.g. it is built from governed run/engine
// data at resolve time). Typed here — never left to a prose SCOPE NOTE — so
// the registration census can prove per-key completeness:
//   manifest required = registered artifact-backed ∪ typed non-artifact,
// with the two sets disjoint (Codex K3 review, #4602 comment 5324455668).
export type ModuleNonArtifactEvidenceKey = Readonly<{
  evidenceKey: string;
  sourceAuthority: string;
  reason: string;
}>;

export type ModuleEvidenceContract<
  ModuleId extends string = string,
  Entries extends Readonly<Record<string, ModuleEvidenceContractEntry>> = Readonly<
    Record<string, ModuleEvidenceContractEntry>
  >,
  KeySpaces extends ModuleContractKeySpaces = ModuleContractKeySpaces,
> = Readonly<{
  moduleId: ModuleId;
  entries: Entries;
  keySpaces: KeySpaces;
  nonArtifactKeys?: readonly ModuleNonArtifactEvidenceKey[];
}>;

type ModuleEvidenceEntryProjection<
  ContextKind extends string,
  SchemaVersion extends string,
  BindingPlaceholder extends string,
> = Omit<
  ModuleEvidenceContractEntry<string, ContextKind, SchemaVersion, BindingPlaceholder>,
  'evidenceKey' | 'stateKey'
>;

export function defineModuleEvidenceEntry<
  const EvidenceKey extends string,
  const ContextKind extends string,
  const SchemaVersion extends string,
  const BindingPlaceholder extends string,
>(
  evidenceKey: EvidenceKey,
  projection: ModuleEvidenceEntryProjection<ContextKind, SchemaVersion, BindingPlaceholder>
): ModuleEvidenceContractEntry<EvidenceKey, ContextKind, SchemaVersion, BindingPlaceholder> {
  return Object.freeze({
    evidenceKey,
    stateKey: evidenceKey,
    ...projection,
  });
}

export function defineModuleEvidenceContract<
  const ModuleId extends string,
  const Entries extends Readonly<Record<string, ModuleEvidenceContractEntry>>,
  const KeySpaces extends ModuleContractKeySpaces,
>(
  moduleId: ModuleId,
  entries: Entries,
  keySpaces: KeySpaces,
  nonArtifactKeys?: readonly ModuleNonArtifactEvidenceKey[],
): ModuleEvidenceContract<ModuleId, Entries, KeySpaces> {
  return Object.freeze({
    moduleId,
    entries: Object.freeze(entries),
    keySpaces: Object.freeze(keySpaces),
    ...(nonArtifactKeys ? { nonArtifactKeys: Object.freeze([...nonArtifactKeys]) } : {}),
  });
}
